import { getDepartment } from "@/grid/config/registry";
import { buildTeamGroups } from "@/grid/data/assemble-grid";
import { fetchGridRawData } from "@/grid/data/fetch-grid-data";
import {
  allMonthStartsInCalendarQuarter,
  allMonthStartsInCalendarYear,
  monthStart,
  quarterStart,
  quarterStartsInCalendarYear,
  weekStartMonday,
} from "@/grid/core/periods";
import {
  RECURRING_WEEK_PERIOD_START,
  RECURRING_WEEK_PERIOD_TYPE,
} from "@/grid/core/target-periods";
import {
  indexMetricConfigs,
  metricsForStorage,
  metricsNeedingExplicitMonth,
  metricsNeedingExplicitQuarter,
} from "@/grid/core/target-policy";
import type { TargetPeriodType, TargetRow } from "@/grid/core/types";
import { buildTargetEditorSheets } from "@/targets/build-sheet-values";
import { fetchTargetMetricConfigs } from "@/targets/api/target-config-api";
import { fetchTargetsForPeriod, fetchTargetsForPeriodStarts } from "@/targets/api/targets-api";
import { useQuery } from "@tanstack/react-query";

function mapTargetRows(
  periodTargets: Array<{
    scope_type: string;
    scope_key: string | null;
    scope_id: number | null;
    metric: string;
    period_type: string;
    period_start: string;
    target_value: number;
  }>,
): TargetRow[] {
  return periodTargets.map((row) => ({
    scope_type: row.scope_type as TargetRow["scope_type"],
    scope_key: row.scope_key,
    scope_id: row.scope_id,
    metric: row.metric,
    period_type: row.period_type as TargetPeriodType,
    period_start: row.period_start,
    target_value: row.target_value,
    effective_from: null,
  }));
}

export interface TargetEditorDataOptions {
  weekFocusStart?: string;
  quarterFocusStart?: string;
  monthScope?: "quarter" | "year";
}

export function useTargetEditorData(
  departmentId: string,
  anchorDate: string,
  options?: TargetEditorDataOptions,
) {
  const department = getDepartment(departmentId);
  const weekFocusStart = options?.weekFocusStart;
  const quarterFocusStart = options?.quarterFocusStart;
  const monthScope = options?.monthScope ?? "quarter";

  return useQuery({
    queryKey: [
      "target-editor",
      departmentId,
      anchorDate,
      weekFocusStart ?? null,
      quarterFocusStart ?? null,
      monthScope,
    ],
    queryFn: async () => {
      if (!department) throw new Error(`Unknown department: ${departmentId}`);

      const anchor = new Date(`${anchorDate}T12:00:00`);
      const monthStartDate = monthStart(anchor);
      const weekStartDate = weekFocusStart ?? weekStartMonday(anchor);
      const quarterStartDate = quarterFocusStart ?? quarterStart(anchor);
      const quarterMonthStarts = allMonthStartsInCalendarQuarter(anchor);
      const monthStarts =
        monthScope === "year" ? allMonthStartsInCalendarYear(anchor) : quarterMonthStarts;
      const yearQuarterStarts = quarterStartsInCalendarYear(anchor);

      const [raw, configs, weekOverrides, recurringTargets, monthTargets, quarterTargets] =
        await Promise.all([
          fetchGridRawData(department),
          fetchTargetMetricConfigs(departmentId).catch(() => []),
          fetchTargetsForPeriod("week", weekStartDate),
          fetchTargetsForPeriod(RECURRING_WEEK_PERIOD_TYPE, RECURRING_WEEK_PERIOD_START),
          fetchTargetsForPeriodStarts("month", monthStarts),
          fetchTargetsForPeriod("quarter", quarterStartDate),
        ]);

      const teams = buildTeamGroups(raw.teams, department);
      const targets = mapTargetRows([
        ...weekOverrides,
        ...recurringTargets,
        ...monthTargets,
        ...quarterTargets,
      ]);
      const configByMetric = indexMetricConfigs(departmentId, department.metrics, configs);
      const sheets = buildTargetEditorSheets(
        department,
        teams,
        targets,
        anchor,
        weekStartDate,
        quarterStartDate,
        configByMetric,
        monthStarts,
      );

      const monthlyMetrics = (() => {
        const seen = new Set<string>();
        const result = [];
        for (const metric of [
          ...metricsForStorage(department.metrics, "month", configByMetric),
          ...metricsNeedingExplicitMonth(department.metrics, configByMetric),
        ]) {
          if (seen.has(metric.id)) continue;
          seen.add(metric.id);
          result.push(metric);
        }
        return result;
      })();
      const quarterlyMetrics = metricsNeedingExplicitQuarter(department.metrics, configByMetric);

      return {
        department,
        teams,
        configs,
        configByMetric,
        sheets,
        weeklyMetrics: metricsForStorage(department.metrics, "week_default", configByMetric),
        monthlyMetrics,
        quarterlyMetrics,
        monthStart: monthStartDate,
        quarterMonthStarts,
        monthStarts,
        yearQuarterStarts,
        weekStart: weekStartDate,
        quarterStart: quarterStartDate,
      };
    },
    enabled: Boolean(department),
  });
}

export function useTargetDepartment(departmentId: string) {
  return getDepartment(departmentId);
}
